import { create } from "zustand";

type Theme = "light" | "dark";

interface ThemeStore {
  theme: Theme;
  toggle: () => void;
}

interface ManualOverride {
  theme: Theme;
  system: Theme;
}

const OVERRIDE_KEY = "themeOverride";
const LEGACY_KEY = "theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function systemTheme(): Theme {
  return window.matchMedia?.(DARK_QUERY).matches ? "dark" : "light";
}

function readOverride(): ManualOverride | null {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(OVERRIDE_KEY) ?? "null");
    return parsed?.theme === "light" || parsed?.theme === "dark" ? parsed : null;
  } catch {
    return null;
  }
}

function initialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  window.localStorage.removeItem(LEGACY_KEY);
  const system = systemTheme();
  const override = readOverride();
  if (override?.system === system) return override.theme;
  window.localStorage.removeItem(OVERRIDE_KEY);
  return system;
}

function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export const useTheme = create<ThemeStore>((set, get) => {
  const theme = initialTheme();
  applyTheme(theme);

  const followSystem = (e: MediaQueryListEvent) => {
    const next: Theme = e.matches ? "dark" : "light";
    window.localStorage.removeItem(OVERRIDE_KEY);
    applyTheme(next);
    set({ theme: next });
  };
  if (typeof window !== "undefined") {
    window.matchMedia?.(DARK_QUERY).addEventListener?.("change", followSystem);
  }

  return {
    theme,
    toggle: () => {
      const next: Theme = get().theme === "dark" ? "light" : "dark";
      const override: ManualOverride = { theme: next, system: systemTheme() };
      window.localStorage.setItem(OVERRIDE_KEY, JSON.stringify(override));
      applyTheme(next);
      set({ theme: next });
    },
  };
});
